import { Box, Skeleton as MantineSkeleton, SkeletonProps, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedSkeletonProps extends SkeletonProps {
  gradient?: boolean;
  gradientVariant?: GradientVariant;
  shape?: 'rectangle' | 'circle' | 'text';
  lines?: number;
}

const Skeleton = forwardRef<HTMLDivElement, ThemedSkeletonProps>(
  ({ gradient = false, gradientVariant = 'primary', shape = 'rectangle', lines, height, width, radius, ...rest }, ref) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const gradientConfig = theme.other.gradients[gradientVariant];

    if (lines) {
      return (
        <Box ref={ref} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: lines }).map((_, i) => (
            <MantineSkeleton
              key={i}
              height={height || 16}
              width={i === lines - 1 ? '60%' : width || '100%'}
              radius={radius || 'sm'}
              {...rest}
            />
          ))}
        </Box>
      );
    }

    if (shape === 'circle') {
      return (
        <MantineSkeleton
          ref={ref}
          height={height || 40}
          width={width || height || 40}
          circle
          {...rest}
        />
      );
    }

    if (gradient) {
      return (
        <Box
          ref={ref}
          style={{
            height: height || 20,
            width: width || '100%',
            borderRadius: radius || theme.radius.sm,
            background: `linear-gradient(90deg, ${gradientConfig.from}20 25%, ${gradientConfig.to}40 50%, ${gradientConfig.from}20 75%)`,
            backgroundSize: '200% 100%',
            animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
          }}
        >
          <style>{`
            @keyframes skeleton-shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
        </Box>
      );
    }

    return (
      <MantineSkeleton
        ref={ref}
        height={height}
        width={width}
        radius={radius}
        {...rest}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export default Skeleton;
